/**
 * apps/battle/client/src/terminal/screens/ResetPasswordScreen.tsx
 *
 * Phase 4.2 M7 — consume password-reset magic link.
 *
 * Mounted by main.tsx when the URL is /reset?token=... The token is hashed
 * server-side and looked up in auth_email_tokens. On success the server
 * auto-logs-in the student (sets session cookie) and returns the same shape
 * as /api/auth/login. We then bounce to / so TerminalApp boots normally.
 */
import React, { useState } from 'react';
import { api, ApiError } from '../../api.js';
import type { LoginResp } from '../../api.js';
import { Spinner, ErrorBanner } from '../../widgets.js';
import { TerminalFrame } from '../components/TerminalFrame.js';
import { KeyBar, type KeyBarItem } from '../components/KeyBar.js';

interface ResetPasswordScreenProps {
  token: string;
}

export function ResetPasswordScreen({ token }: ResetPasswordScreenProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password.length < 10) { setError('Password must be at least 10 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setError(null);
    setLoading(true);
    try {
      const resp = await api.post<LoginResp>('/api/auth/reset', { token, password });
      if (resp.authenticated) {
        // Server set the session cookie; bounce to root and let the app boot.
        window.location.href = '/terminal';
      } else {
        setError('Reset failed — unexpected response.');
        setLoading(false);
      }
    } catch (err) {
      setLoading(false);
      if (err instanceof ApiError && err.status === 400) {
        setError('This reset link is invalid or has expired. Request a new one.');
      } else if (err instanceof ApiError && err.status === 422) {
        const body = err.body as { reason?: string } | undefined;
        setError(body?.reason || 'That password is too weak — choose a stronger one.');
      } else if (err instanceof ApiError && err.status === 429) {
        setError('Too many requests. Please wait a moment.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
  }

  const keybar: KeyBarItem[] = [
    { label: 'Enter', description: 'set new password' },
    { label: 'Esc', description: 'back to login', onActivate: () => { window.location.href = '/terminal'; } },
  ];

  return (
    <TerminalFrame
      title={<>ECONQUEST &nbsp;&nbsp;─&nbsp;&nbsp;SET NEW PASSWORD</>}
      keybar={<KeyBar items={keybar} />}
    >
      <form onSubmit={handleSubmit} style={{ maxWidth: 480, margin: '0 auto', paddingTop: 24 }}>
        <p style={{ color: '#cdd5e0', marginTop: 0, lineHeight: 1.6 }}>
          Choose a new password. You'll be signed in automatically once it's set.
        </p>

        <div className="form-field">
          <label className="form-label" htmlFor="rp-pw">New password</label>
          <input
            id="rp-pw"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 10 characters"
            minLength={10}
            required
            autoFocus
          />
        </div>

        <div className="form-field">
          <label className="form-label" htmlFor="rp-pw2">Confirm new password</label>
          <input
            id="rp-pw2"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Repeat password"
            minLength={10}
            required
          />
        </div>

        {error && <ErrorBanner msg={error} />}

        <button className="btn btn-primary w-full mt-4" type="submit" disabled={loading}>
          {loading ? <Spinner size={16} /> : 'Set new password'}
        </button>
      </form>
    </TerminalFrame>
  );
}
